/**
 * @file useJobWizardContext.tsx
 *
 * @fileoverview Custom hook that handles getting information of job bundles and other relevant information for the job
 * posting wizard.
 */

import { useRouter } from 'next/router';
import {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
import { JobBundle } from '~/jobiqo/models/jobs';
import GET_JOB_BUNDLES from '~/jobiqo/components/Job/Wizard/Steps/Bundles/graphql/queries/jobBundles.gql';
import GET_JOB_UPGRADES from '~/jobiqo/components/Job/Wizard/Steps/Upgrades/graphql/jobUpgrades.gql';
import { useQuery } from '@apollo/client';
import { UserContext } from '~/jobiqo/context/user';
import GET_ORGANIZATION_BY_ID from '~/jobiqo/components/SelfService/graphql/queries/getOrganizationProfileById.gql';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

export interface JobWizardContextType {
  /**
   * All the job bundles for the wizard.
   */
  bundles: JobBundle[];
  /**
   * If the wizard should show the bundles step.
   */
  shouldShowBundlesStep: boolean;
  /**
   * If the wizard should show the upgrades step.
   */
  shouldShowUpgradesStep: boolean;
  /**
   * Set new value if the wizard should show the upgrades step.
   */
  setShouldShowUpgradesStep: Dispatch<SetStateAction<boolean>>;
  /**
   * If the wizard should show the destination step.
   */
  shouldShowDestinationStep: boolean;
  /**
   * If the wizard should show a separate destination upgrades step.
   */
  shouldShowDestinationUpgradesStep: boolean;
  /**
   * The list of selected subscriptions.
   */
  subscriptions: number[];
  /**
   * Set new value for the list of selected subscriptions.
   */
  setSubscriptions: Dispatch<SetStateAction<number[]>>;
}

const JobWizardWizardContext = createContext<JobWizardContextType | null>(null);
export const JobWizardContextProvider = ({ children }) => {
  const router = useRouter();
  const { JOBS } = useFrontendConfig();
  const { currentOrganizationProfile } = useContext(UserContext);
  const SHOW_DESTINATION_STEP = JOBS.JOB_POSTING_WIZARD_SHOW_DESTINATION;
  const SHOW_UPGRADES_IN_DESTINATION_STEP =
    JOBS.SHOW_UPGRADES_IN_DESTINATION_STEP;
  const DESTINATION_SEPARATE_UPGRADES_STEP =
    JOBS.DESTINATION_PAGE_CONFIG?.SEPARATE_UPGRADES_STEP;
  const JOB_POSTING_JOB_BUNDLES_SHOW_FROM_ALL_ORG_FRONTENDS =
    JOBS.JOB_POSTING_JOB_BUNDLES_SHOW_FROM_ALL_ORG_FRONTENDS;
  const [orgFrontends, setOrgFrontends] = useState<string[]>([]);

  const [shouldShowUpgradesStep, setShouldShowUpgradesStep] = useState<boolean>(
    true,
  );
  const [shouldShowDestinationStep, setShouldShowDestinationStep] = useState<
    boolean
  >(false);

  const [
    shouldShowDestinationUpgradesStep,
    setShouldShowDestinationUpgradesStep,
  ] = useState<boolean>(false);

  const [subscriptions, setSubscriptions] = useState<number[]>([]);

  const { data: orgData } = useQuery(GET_ORGANIZATION_BY_ID, {
    variables: {
      organizationProfileId: currentOrganizationProfile?.id,
    },
    skip: !SHOW_DESTINATION_STEP || !currentOrganizationProfile?.id,
  });

  // Update frontends when organization data changes.
  useEffect(() => {
    if (SHOW_DESTINATION_STEP) {
      if (orgData?.organizationProfileById?.content?.frontends) {
        setOrgFrontends(orgData?.organizationProfileById?.content?.frontends);
      }
    }
  }, [orgData, SHOW_DESTINATION_STEP]);

  useEffect(() => {
    if (SHOW_DESTINATION_STEP) {
      setShouldShowDestinationStep(true);
      // Do not show the upgrades step if the destination step is active and the flag for showing upgrades in
      // destination step is true.
      if (SHOW_UPGRADES_IN_DESTINATION_STEP) {
        setShouldShowUpgradesStep(false);
      }
    }
  }, [SHOW_DESTINATION_STEP, SHOW_UPGRADES_IN_DESTINATION_STEP]);

  // Set the destination upgrades step visibility based on config and upgrades availability.
  useEffect(() => {
    if (SHOW_DESTINATION_STEP && DESTINATION_SEPARATE_UPGRADES_STEP) {
      setShouldShowDestinationUpgradesStep(true);
    }
  }, [SHOW_DESTINATION_STEP, DESTINATION_SEPARATE_UPGRADES_STEP]);

  const { data } = useQuery(GET_JOB_BUNDLES, {
    variables: {
      locale: router.locale,
      ...(JOB_POSTING_JOB_BUNDLES_SHOW_FROM_ALL_ORG_FRONTENDS && {
        frontends: orgFrontends,
      }),
    },
    fetchPolicy: 'no-cache',
  });

  // @ts-ignore
  const { data: jobUpgradesData } = useQuery(GET_JOB_UPGRADES, {
    variables: {
      locale: router.locale,
    },
    onCompleted: (completedJobUpgradesData) => {
      setShouldShowUpgradesStep(
        completedJobUpgradesData?.jobUpgrades?.length > 0 &&
          !SHOW_UPGRADES_IN_DESTINATION_STEP,
      );
    },
  });

  return (
    <JobWizardWizardContext.Provider
      value={{
        bundles: data?.jobBundles ?? [],
        shouldShowBundlesStep: data?.jobBundles?.length > 0,
        shouldShowUpgradesStep,
        setShouldShowUpgradesStep,
        shouldShowDestinationStep,
        shouldShowDestinationUpgradesStep,
        subscriptions,
        setSubscriptions,
      }}
    >
      {children}
    </JobWizardWizardContext.Provider>
  );
};

export const useJobWizardContext = (): JobWizardContextType =>
  useContext(JobWizardWizardContext) as JobWizardContextType;
